import { useState, useRef, useEffect } from 'react'
import { Card, CardHeader, Button, Badge } from '../../components/ui'
import { Play, Terminal, CheckCircle2, XCircle, AlertCircle, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const TEST_SUITES = [
    { id: 'auth', name: 'Authentication', description: 'Tests login, logout, and token management.' },
    { id: 'tournament', name: 'Tournament Creation', description: 'Tests the wizard flow for new tournaments.' },
    { id: 'teams', name: 'Teams Management', description: 'Tests adding, editing, and deleting teams.' },
    { id: 'matches', name: 'Matches & Scores', description: 'Tests general score entry and match locks.' },
    { id: 'standings', name: 'Standings Logic', description: 'Tests points, goals, and tie-breaker sorting.' },
    { id: 'bracket', name: 'Knockout Bracket', description: 'Tests auto-promotion, bracket shuffling, and finals.' },
    { id: 'esports', name: 'Esports System Flow', description: 'Tests solo and team esports creation, labels, and wizard options.' },
    { id: 'ladder', name: 'Ladder System Flow', description: 'Tests ongoing rank shuffling, match swapping, and history logs.' },
    { id: 'padel', name: 'Racket Sports Flow', description: 'Tests sets/games configuration, set-scoring, and standings calculations.' },
]


export const TestRunnerPage = () => {
    const { t } = useTranslation()
    const [runningTest, setRunningTest] = useState(null)
    const [logs, setLogs] = useState([])
    const [status, setStatus] = useState('idle') // idle, running, success, failed
    const terminalRef = useRef(null)

    // Auto-scroll terminal to bottom
    useEffect(() => {
        if (terminalRef.current) {
            terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
        }
    }, [logs]);

    const runTest = (testId) => {
        if (status === 'running') return;

        setRunningTest(testId);
        setLogs([{ type: 'system', msg: `Initializing test suite: ${testId}.spec.js...` }]);
        setStatus('running');

        // Make request to our custom Vite plugin API endpoint
        const evtSource = new EventSource(`/api/run-test?test=${testId}`);

        evtSource.onmessage = (event) => {
            const data = JSON.parse(event.data);

            setLogs((prev) => [...prev, data]);

            if (data.type === 'done') {
                evtSource.close();
                if (data.code === 0) {
                    setStatus('success');
                    setLogs((prev) => [...prev, { type: 'success', msg: '\n✅ TEST SUITE PASSED SUCCESSFULLY' }]);
                } else {
                    setStatus('failed');
                    setLogs((prev) => [...prev, { type: 'error', msg: `\n❌ TEST SUITE FAILED (Exit Code: ${data.code})` }]);
                }
                setRunningTest(null);
            }
        };

        evtSource.onerror = (err) => {
            console.error("EventSource Error:", err);
            evtSource.close();
            setStatus('failed');
            setLogs((prev) => [...prev, { type: 'error', msg: '\n⚠️ Connection to Test Runner API lost. Is Vite running?' }]);
            setRunningTest(null);
        };
    }

    const getStatusBadge = () => {
        switch (status) {
            case 'running': return <Badge variant="warning" className="animate-pulse">Running...</Badge>
            case 'success': return <Badge variant="success">Passed</Badge>
            case 'failed': return <Badge variant="error">Failed</Badge>
            default: return <Badge variant="neutral">Ready</Badge>
        }
    }

    return (
        <div className="min-h-screen pb-12">
            <div className="premium-container">

                {/* Header */}
                <div className="mb-8 animate-slideUp">
                    <div className="flex items-center gap-4 mb-3">
                        <div className="p-3 rounded-2xl bg-indigo-100 text-indigo-600 shadow-sm">
                            <Terminal size={32} />
                        </div>
                        <div>
                            <h1 className="text-4xl font-bold text-gray-900 leading-tight">
                                Test Runner
                            </h1>
                            <p className="text-lg text-gray-600 mt-1">
                                Execute automated End-to-End tests directly from the dashboard.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                    {/* Left Column: Test Suites List */}
                    <div className="lg:col-span-1 space-y-4">
                        <h2 className="text-xl font-bold px-1 flex items-center gap-2 mb-4">
                            <CheckCircle2 size={20} className="text-gray-400" /> Available Suites
                        </h2>

                        {TEST_SUITES.map((suite) => (
                            <Card
                                key={suite.id}
                                className={`transition-all duration-200 ${runningTest === suite.id ? 'border-indigo-500 ring-1 ring-indigo-500 shadow-md' : 'hover:border-gray-300'}`}
                            >
                                <div className="p-4">
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="font-semibold text-gray-900">{suite.name}</h3>
                                        {runningTest === suite.id && (
                                            <RefreshCw size={16} className="text-indigo-500 animate-spin" />
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-500 mb-4 h-10">{suite.description}</p>
                                    <Button
                                        onClick={() => runTest(suite.id)}
                                        disabled={status === 'running'}
                                        variant={runningTest === suite.id ? "primary" : "secondary"}
                                        className="w-full justify-center flex items-center gap-2"
                                    >
                                        <Play size={16} />
                                        {runningTest === suite.id ? 'Running...' : 'Run Test'}
                                    </Button>
                                </div>
                            </Card>
                        ))}
                    </div>

                    {/* Right Column: Terminal Output */}
                    <div className="lg:col-span-2">
                        <Card className="h-full flex flex-col overflow-hidden border-gray-800 bg-gray-950 shadow-2xl">
                            <div className="bg-gray-900 px-4 py-3 border-b border-gray-800 flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                    <div className="flex gap-1.5">
                                        <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
                                        <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
                                        <div className="w-3 h-3 rounded-full bg-green-500/80"></div>
                                    </div>
                                    <span className="text-gray-400 text-sm ml-4 font-mono font-medium flex items-center gap-2">
                                        <Terminal size={14} /> terminal
                                    </span>
                                </div>
                                {getStatusBadge()}
                            </div>

                            <div
                                ref={terminalRef}
                                className="flex-1 p-5 overflow-y-auto font-mono text-sm leading-relaxed"
                                style={{ minHeight: '500px', maxHeight: '700px' }}
                            >
                                {logs.length === 0 ? (
                                    <div className="text-gray-600 flex flex-col items-center justify-center h-full gap-3 mt-12 mb-12">
                                        <Terminal size={48} className="opacity-20" />
                                        <p>Select a test suite from the left to view output</p>
                                    </div>
                                ) : (
                                    <div className="space-y-1">
                                        {logs.map((log, i) => {
                                            // Format coloring based on message type or content
                                            let textColor = 'text-gray-300';
                                            let text = log.msg || '';

                                            if (log.type === 'error' || text.includes('Error:') || text.includes('failed')) {
                                                textColor = 'text-red-400';
                                            } else if (log.type === 'success' || text.includes('passed') || text.includes('✓')) {
                                                textColor = 'text-green-400 font-semibold';
                                            } else if (log.type === 'system' || log.type === 'start') {
                                                textColor = 'text-indigo-400 font-semibold';
                                            }

                                            // Remove terminal color ansi codes if present for cleaner web view (basic regex)
                                            // Added ? to cleanly strip codes
                                            let cleanText = text;
                                            if (typeof cleanText === 'string') {
                                                cleanText = cleanText.replace(/[\u001b\u009b][[()#;?]*(?:[0-9]{1,4}(?:;[0-9]{0,4})*)?[0-9A-ORZcf-nqry=><]/g, '');
                                            }

                                            return (
                                                <div key={i} className={`whitespace-pre-wrap break-words ${textColor}`}>
                                                    {cleanText}
                                                </div>
                                            )
                                        })}
                                        {status === 'running' && (
                                            <div className="text-gray-500 animate-pulse mt-2">_</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </Card>
                    </div>

                </div>
            </div>
        </div>
    )
}
